<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">In this activity, you will look at some information and complete 3 tasks.</p>
    <h4 class="mb-5">The Rule of Thirds</h4>
    <p class="mb-5">
      The Rule of Thirds is a common technique used by photographers to frame their photographs.
    </p>
    <p class="mb-5">
      Following the Rule of Thirds means the photographer divides their frame into an equal 3x3 grid
      with two horizontal lines and two vertical lines intersecting at four points. The photographer
      places the subject either on the right line or the left line of the frame to create good
      composition like in the example below:
    </p>

    <centered-image-wrapper
      :image="require('@/assets/images/experiences/creativeindustries/activity_41.png')"
      alt="Rule of Thirds example: a black and white photo of an ice hockey player kneeling on the ice, overlaid with a 3x3 grid of two horizontal and two vertical lines; the player is positioned along the right-hand vertical line."
    />

    <p class="mb-5">
      Each of the four intersections of the horizontal and vertical lines is a point of potential
      interest for the viewer. Many smartphone cameras and other digital cameras come with settings
      allowing you to display a Rule of Thirds grid onscreen while taking your pictures.
    </p>
    <p class="mb-5">To learn more about the Rule of Thirds in action watch the following videos:</p>

    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />
    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />

    <h4 class="mb-5">The Rule of Thirds Challenge</h4>
    <p class="mb-5">
      Now that you understand how the Rule of Thirds works, the next step is to practise
      incorporating it into your own photographs.
    </p>
    <p class="mb-5">
      In this challenge, you are going to take a series of photographs on different subjects
      employing the Rule of Thirds in both portrait and landscape orientations.
    </p>

    <h4 class="mb-5">Task 1: Apply The Rule of Thirds to Portraits</h4>
    <p class="mb-5">
      First, choose a subject to take a portrait photo of. This could be a friend or family member.
      You may even take a self-portrait.
    </p>
    <p class="mb-5">
      As well as following the Rule of Thirds when taking your portrait photos, take on board the
      advice in the short video below.
    </p>
    <responsive-youtube-wrapper :video="videos[3].video" :image="videos[3].image" />

    <h4 class="mb-5">Now, let’s get started:</h4>
    <ul class="default-list">
      <li>
        Use the grid on your digital camera (if available) and take multiple photos of your subject,
        putting it on the different third-lines each time.
      </li>
      <li>Then, photograph the subject right in the centre of the frame.</li>
      <li>Shoot lots and lots of photos and upload them onto your computer.</li>
    </ul>
    <p class="mb-5">
      As no human face is perfectly symmetrical, applying the Rule of Thirds helps to create
      interesting portraits.
    </p>
    <p class="mb-5">Now, take a look at the different images you’ve taken.</p>
    <p class="mb-5">Compare them and decide which ones you like best.</p>
    <p class="mb-5">
      Repeat this process until you’ve taken portrait photos of 5 different subjects.
    </p>
    <p class="mb-5">
      <u>Note:</u> If you don’t have access to 5 different subjects, you may ask your model (or
      yourself!) to change clothes, hairstyle, etc for a new shoot.
    </p>
    <p class="mb-5">
      Now, upload your best Portrait shot where you have applied the above principles.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Best Portrait'"
      index="1"
      filetype="photo"
    />

    <p class="mb-5">Some Further Helpful Tips for Shooting Portrait Photographs</p>
    <ul class="default-list">
      <li>Align your subject’s right or left eye on one of the upper two intersection points.</li>
      <li>
        Align your subject’s shoulders along the lower-third grid line, giving the subject a natural
        and relaxed appearance.
      </li>
      <li>
        Draw your viewer into your portrait by placing the subject at a subtle angle toward the
        other empty quadrants in the grid.
      </li>
    </ul>
    <h4 class="mb-5">Task 2: Apply The Rule of Thirds to Landscapes</h4>
    <p class="mb-5">
      To get started, choose a scene to photograph. Don’t worry if you can’t travel too far from
      home at the moment, anywhere outside in the built or natural environment will do for this
      activity.
    </p>
    <p class="mb-5">
      As well as following the Rule of Thirds when taking your landscape photos, take on board the
      advice in the short video below.
    </p>
    <responsive-youtube-wrapper :video="videos[4].video" :image="videos[4].image" />

    <p class="mb-5">Now, let’s get started:</p>
    <ul class="default-list">
      <li>
        Use the grid on your digital camera (if available) and take multiple landscape photos, using
        different third-lines each time for the focal point.
      </li>
      <li>Then, photograph the focal point right in the centre of the frame.</li>
      <li>Shoot lots and lots of photos and upload them onto your computer.</li>
    </ul>

    <p class="mb-5">Now, take a look at the different images you’ve taken.</p>
    <p class="mb-5">Compare them and decide which ones you like best.</p>
    <p class="mb-5">
      Repeat this process until you’ve taken landscape photos with 5 different focal points.
    </p>
    <p class="mb-5">
      Now, upload your best Landscape shot where you have applied the above principles.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Best Landscape'"
      index="2"
      filetype="photo"
    />

    <p class="mb-5"><i>Some Further Helpful Tips for Shooting Landscape Photographs</i></p>
    <ul class="default-list">
      <li>
        Create a sense of expansiveness by positioning the horizon line along with the lower third
        of the grid, this draws the viewer's eye to the sky above.
      </li>
      <li>
        Position the horizon along the upper third to draw the eye to the foreground, this creates a
        sense of proximity with the landscape.
      </li>
      <li>
        Place an interesting natural feature (e.g. a mountain peak or waterfall) on one of the four
        intersection points to create a focal point.
      </li>
    </ul>

    <h4 class="mb-5">Task 3: Applying The Rule of Thirds During the Editing Process</h4>
    <p class="mb-5">
      While photographers often use The Rule of Thirds to frame their photographs while shooting
      them, they sometimes apply the rule during the editing process by using ‘cropping’ or
      ‘reframing’ features in their editing software. To do this simply adjust the horizontal or
      vertical skew of the image so that the lines align with the grid and then save it.
    </p>
    <p class="mb-5">
      Sometimes a feature in the software will allow you to superimpose the grid lines over your
      image to help with this. Other times, you’ll have to do it by sight.
    </p>
    <p class="mb-5">
      To get some practice, practice uploading some of the unframed photos in your phone’s gallery
      to Canva and using the crop and resize features.
    </p>
    <p class="mb-5">
      To get started you need to set up a free Canva account. To do this, go to
      <a href="https://www.canva.com" target="_blank">https://www.canva.com</a> and use your email
      to set up a free Canva account and then log in.
    </p>
    <p class="mb-5">Now go to:</p>
    <p class="mb-5">
      <a href="https://www.canva.com/photo-editor/" target="_blank"
        >https://www.canva.com/photo-editor/</a
      >
    </p>
    <p class="mb-5">Here, you’ll find out more about how to edit your images using Canva.</p>
    <p class="mb-5">
      Now, go through your phone’s gallery of photos that you’ve taken in the past. Upload some of
      these to Canva and edit them in the programme to improve them. Use The Rule of Thirds and any
      of the other techniques you’ve been working on lately.
    </p>
    <p class="mb-5">
      When you’ve finished, choose one photo that you have been able to significantly improve using
      all the techniques you have learned. Below you can upload your “Before Photo” (the original
      photo you have not yet worked on) and your “After Photo” (the same photo after you have worked
      your magic on it).
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Before Photo'"
      index="3"
      filetype="photo"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_4']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload After Photo'"
      index="4"
      filetype="photo"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/creativeindustries/rule_of_thirds_splash.jpg'),
          video: 'https://www.youtube.com/embed/UrCM4_NRmvA',
        },
        2: {
          image: require('@/assets/images/experiences/creativeindustries/rule_of_thirds_2_splash.jpg'),
          video: 'https://www.youtube.com/embed/U9izgAqa-fA',
        },
        3: {
          image: require('@/assets/images/experiences/creativeindustries/portraits_with_smartphone_splash.jpg'),
          video: 'https://www.youtube.com/embed/NALsNo0qHew',
        },
        4: {
          image: require('@/assets/images/experiences/creativeindustries/landscapes_with_smartphone_splash.jpg'),
          video: 'https://www.youtube.com/embed/FcRLmDCjXdY',
        },
      },
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
        uploaded_file_4: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    for (let i = 1; i <= 4; i++) {
      if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
        this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
      }
    }
  },
};
</script>
